/**
 * Preview step for the Leads page "CSV / Excel import" (2026-10-07).
 *
 * Shows which file column feeds which lead field (each one re-pointable),
 * how many rows will import and how many are skipped, and only saves when the
 * user clicks Import. Afterwards it shows "X imported, Y skipped (no phone),
 * Z already existed" from the bulk API's answer.
 */
import { useMemo, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { api } from '@/utils/api';
import {
  buildImportPlan, IMPORT_FIELD_LABELS, IMPORT_FIELD_OPTIONS,
  type ImportColumn, type ImportField, type Table,
} from './leadFileImport';

interface ImportOutcome {
  created: number;
  /** 2026-10-07 tour bug 4 — the uploader the new leads were assigned to. */
  assignedToName: string | null;
  existing: number;
  existingLeads: { name: string | null; phone: string }[];
}

const CHUNK = 200;

export function LeadFileImportPreview({
  fileName, table, initialColumns, onClose, onImported,
}: {
  fileName: string;
  table: Table;
  initialColumns: ImportColumn[];
  onClose: () => void;
  onImported: () => void;
}) {
  const [columns, setColumns] = useState<ImportColumn[]>(initialColumns);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<ImportOutcome | null>(null);
  const plan = useMemo(() => buildImportPlan(table, columns), [table, columns]);
  const hasPhoneColumn = columns.some((c) => c.field === 'phone');

  const setField = (index: number, field: ImportField) =>
    setColumns((cs) => cs.map((c) => (c.index === index ? { ...c, field } : c)));

  const runImport = async () => {
    setSaving(true);
    setError(null);
    const total: ImportOutcome = { created: 0, assignedToName: null, existing: 0, existingLeads: [] };
    try {
      for (let i = 0; i < plan.rows.length; i += CHUNK) {
        const res = await api.post('/lead-integrations/lead-imports/bulk', { imports: plan.rows.slice(i, i + CHUNK) });
        const d = res.data?.data ?? {};
        total.created += d.created ?? d.count ?? 0;
        total.existing += d.existing ?? 0;
        total.assignedToName = d.assignedToName ?? total.assignedToName;
        total.existingLeads.push(...(d.existingLeads ?? []));
      }
      setOutcome(total);
      onImported();
    } catch (err: any) {
      if (total.created > 0) setOutcome(total);
      setError(err?.response?.data?.error || err?.response?.data?.message || 'Import failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 rounded-lg border border-[var(--border-secondary)] bg-[var(--bg-inset)] p-4" data-testid="lead-file-import-preview">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">Check the columns — {fileName}</h3>
        <button onClick={onClose} className="rounded p-1 text-[var(--text-muted)] hover:bg-[var(--bg-surface)]" aria-label="Close preview"><X size={16} /></button>
      </div>

      {outcome ? (
        <div className="space-y-2 text-sm">
          <p className="font-medium text-green-400">
            {outcome.created} imported, {plan.skippedNoPhone} skipped (no phone)
            {plan.skippedDuplicateInFile > 0 ? `, ${plan.skippedDuplicateInFile} repeated in the file` : ''}
            , {outcome.existing} already existed
          </p>
          {outcome.created > 0 && (
            <p className="text-xs text-[var(--text-primary)]" data-testid="lead-import-assigned">
              The {outcome.created} new {outcome.created === 1 ? 'lead is' : 'leads are'} assigned to you
              {outcome.assignedToName ? ` (${outcome.assignedToName})` : ''} — find {outcome.created === 1 ? 'it' : 'them'} in your Leads list.
            </p>
          )}
          {outcome.existingLeads.length > 0 && (
            <p className="text-xs text-[var(--text-muted)]">
              Already in AURA: {outcome.existingLeads.map((l) => `${l.name || 'no name'} (${l.phone.slice(-10)})`).join(', ')}
            </p>
          )}
          {error && <p className="text-xs text-red-400">Stopped part-way: {error}</p>}
          <button onClick={onClose} className="rounded-lg border border-[var(--border-secondary)] px-3 py-1.5 text-xs text-[var(--text-primary)]">Done</button>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="text-[var(--text-muted)]">
                <tr><th className="py-1 pr-3">Column in your file</th><th className="py-1 pr-3">Example values</th><th className="py-1">Goes into</th></tr>
              </thead>
              <tbody>
                {columns.map((c) => (
                  <tr key={c.index} className="border-t border-[var(--border-secondary)]">
                    <td className="py-1 pr-3 text-[var(--text-primary)]">{c.label}</td>
                    <td className="max-w-[260px] truncate py-1 pr-3 text-[var(--text-muted)]">{c.samples.join(' · ') || '—'}</td>
                    <td className="py-1">
                      <select
                        aria-label={`Field for ${c.label}`}
                        value={c.field}
                        onChange={(e) => setField(c.index, e.target.value as ImportField)}
                        className="rounded border border-[var(--border-secondary)] bg-[var(--bg-surface)] px-2 py-1 text-xs text-[var(--text-primary)]"
                      >
                        {IMPORT_FIELD_OPTIONS.map((f) => <option key={f} value={f}>{IMPORT_FIELD_LABELS[f]}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-3 space-y-1 text-xs text-[var(--text-muted)]">
            <p>
              <strong className="text-[var(--text-primary)]">{plan.totalRows}</strong> rows in the file ·{' '}
              <strong className="text-green-400">{plan.rows.length}</strong> will import ·{' '}
              <strong className="text-yellow-400">{plan.skippedNoPhone}</strong> skipped (no 10-digit phone)
              {plan.skippedDuplicateInFile > 0 && <> · <strong className="text-yellow-400">{plan.skippedDuplicateInFile}</strong> repeated in the file</>}
            </p>
            {plan.skipped.length > 0 && (
              <p>Skipped: {plan.skipped.slice(0, 8).map((s) => `row ${s.line} ${s.name || ''}`.trim()).join(', ')}{plan.skipped.length > 8 ? '…' : ''}</p>
            )}
            <p>
              {plan.usesDefaultSource
                ? 'No source column — these leads are saved with source Manual.'
                : 'Source comes from your source column (blank cells → Manual).'}
              {' '}New leads are assigned to you. A phone that is already in AURA is not added again; it is listed after the import.
            </p>
            {!hasPhoneColumn && <p className="text-red-400">Pick the column that holds the phone number — rows without a phone cannot be imported.</p>}
          </div>

          {error && <p className="mt-2 text-xs text-red-400">{error}</p>}
          <div className="mt-3 flex gap-2">
            <button
              onClick={runImport}
              disabled={saving || plan.rows.length === 0}
              className="flex items-center gap-1 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
            >
              {saving && <Loader2 size={12} className="animate-spin" />}
              Import {plan.rows.length} leads
            </button>
            <button onClick={onClose} disabled={saving} className="rounded-lg border border-[var(--border-secondary)] px-3 py-1.5 text-xs text-[var(--text-primary)]">Cancel</button>
          </div>
        </>
      )}
    </div>
  );
}
